// frontend/src/components/QuestionComponent.jsx
import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

function QuestionComponent({ question, chapterId, contentBlockId, existingAnswer, onAnswerChange }) {
  const [answer, setAnswer] = useState(() => {
    if (question.type === 'checkbox') {
      return Array.isArray(existingAnswer)
        ? existingAnswer
        : existingAnswer
        ? [existingAnswer]
        : [];
    }
    return existingAnswer || '';
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAuthenticated, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [saveStatus, setSaveStatus] = useState(''); // For displaying "Saving..." or "Saved."
  const firstRender = useRef(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Autosave effect
  useEffect(() => {
    // Skip the effect on the first render
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    
    if (answer === undefined || answer === null) return;

    // Update parent's state using the contentBlockId
    if (onAnswerChange) {
      onAnswerChange(contentBlockId, answer);
    }

    const timer = setTimeout(() => {
      setSaveStatus('Saving...');
      saveAnswer();
    }, 500);

    return () => clearTimeout(timer);
  }, [answer]);

  const saveAnswer = async () => {
    try {
      setIsSubmitting(true);
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/user/submit-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        // Send contentBlockId instead of contentBlockIndex
        body: JSON.stringify({ chapterId, contentBlockId, answer }),
      });

      if (!res.ok) throw new Error('Failed to save answer');
      setSaveStatus('Saved.');
    } catch (err) {
      setSaveStatus('Error saving answer');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render 'multiple_choice' question
  if (question.type === 'multiple_choice') {
    return (
      <div className="font-body"> {/* Base font style */}
        <p className="font-body text-3xl mb-8 text-gray-800">{question.prompt}</p>
        <ul className="list-none space-y-3"> {/* Adds vertical spacing between options */}
          {question.options.map((option, idx) => (
            <li key={idx}>
              <label
                className={`
                  flex items-center p-4 border rounded-lg cursor-pointer
                  transition-all duration-150 ease-in-out
                  focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#51c0a6]
                  ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}
                  ${answer === option // For radio, answer is a single value
                    ? 'bg-[#51c0a6] border-[#51c0a6] text-white shadow-md'
                    : 'bg-white hover:bg-gray-50 border-gray-300 text-gray-700 hover:border-gray-400'
                  }
                `}
              >
                <input
                  type="radio"
                  name={`question_${contentBlockId}`}
                  value={option}
                  checked={answer === option}
                  onChange={() => !isSubmitting && setAnswer(option)}
                  disabled={isSubmitting}
                  className="sr-only"
                />
                <span className={`
                  flex-shrink-0 w-5 h-5 mr-4 border-2 rounded-full flex items-center justify-center
                  ${answer === option ? 'border-white bg-white' : 'border-gray-400 bg-white'}
                `}>
                  {answer === option && (
                    <span className="w-2.5 h-2.5 bg-[#51c0a6] rounded-full"></span>
                  )}
                </span>
                <span className="text-3xl">
                  {option}
                </span>
              </label>
            </li>
          ))}
        </ul>
        <p className="text-sm mt-8 h-5 text-gray-500">{saveStatus || (isSubmitting ? 'Saving...' : '')}</p>
      </div>
    );
  }

  // Render 'checkbox' question
  if (question.type === 'checkbox') {
    const handleCheckboxChange = (option) => {
      setAnswer((prev) => {
        const newAnswer = Array.isArray(prev) ? [...prev] : [];
        if (newAnswer.includes(option)) {
          return newAnswer.filter((o) => o !== option);
        } else {
          newAnswer.push(option);
          return newAnswer;
        }
      });
    };

    return (
      <div className="font-body"> {/* Base font style */}
        <p className="font-body text-3xl mb-8 text-gray-800">{question.prompt}</p>
        <ul className="list-none space-y-3"> {/* Adds vertical spacing between options */}
          {question.options.map((option, idx) => {
            const isChecked = Array.isArray(answer) && answer.includes(option);
            return (
              <li key={idx}>
                <label
                  className={`
                    flex items-center p-4 border rounded-lg cursor-pointer
                    transition-all duration-150 ease-in-out
                    focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#51c0a6]
                    ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}
                    ${isChecked
                      ? 'bg-[#51c0a6] border-[#51c0a6] text-white shadow-md' // Selected state
                      : 'bg-white hover:bg-gray-50 border-gray-300 text-gray-700 hover:border-gray-400' // Default state
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    value={option}
                    checked={isChecked}
                    onChange={() => !isSubmitting && handleCheckboxChange(option)}
                    disabled={isSubmitting}
                    className="sr-only" // Visually hide the default checkbox
                  />
                  {/* Custom checkbox visual indicator */}
                  <span className={`
                    flex-shrink-0 w-5 h-5 mr-4 border-2 rounded flex items-center justify-center
                    ${isChecked ? 'border-white bg-white' : 'border-gray-400 bg-white'}
                  `}>
                    {isChecked && (
                      // Checkmark icon (simple SVG or a character)
                      <svg className="w-3 h-3 text-[#51c0a6]" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </span>
                  <span className="text-3xl"> {/* Option text */}
                    {option}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
        {/* Show save status */}
        <p className="text-sm mt-8 h-5 text-gray-500">{saveStatus || (isSubmitting ? 'Saving...' : '')}</p>
      </div>
    );
  }

  // Render 'short_answer' question
  if (question.type === 'short_answer') {
    return (
      <div className="font-body"> {/* Base font style */}
        <p className="font-body text-3xl mb-6 text-gray-800">{question.prompt}</p>
        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          className={`
            w-full p-3 border border-gray-300 rounded-lg 
            font-body text-3xl text-gray-700
            transition-colors duration-150 ease-in-out
            hover:border-gray-400
            focus:outline-none
      `}
          rows={8} // Keep or adjust as needed
          placeholder="Type your answer here..."
        />
        <div className="text-sm mt-8 h-5 text-gray-500"> {/* Consistent save status styling */}
          {saveStatus || (isSubmitting ? 'Saving...' : '')}
        </div>
      </div>
    );
  }

  return null;
}

export default QuestionComponent;
